import { isNativeApp } from '@helpers/isNativeApp';
import { isPWA } from '@helpers/isPWA';

export type Platform = 'browser' | 'ios' | 'android' | 'pwa';

/**
 * Sniffs the device OS from the base user-agent.
 * Returns null when neither can be determined — e.g. desktop browsers, or
 * iPadOS Safari, which reports a macOS ("Macintosh") user-agent by default.
 */
const sniffOsFromUa = (ua: string): 'ios' | 'android' | null => {
    if (/android/i.test(ua)) return 'android';
    if (/iphone|ipad|ipod/i.test(ua)) return 'ios';
    return null;
};

/**
 * Resolves the surface the app is running on, for analytics segmentation.
 * Values: 'browser' | 'ios' | 'android' | 'pwa'.
 *
 * Precedence:
 *  1. Inside the native shell   -> 'ios' | 'android'
 *  2. Installed PWA             -> 'pwa'
 *  3. Mobile web (iPhone/Android browser) -> 'ios' | 'android'
 *  4. Otherwise (desktop)       -> 'browser'
 *
 * NOTE: 'ios'/'android' is NOT exclusive to the native app — mobile web
 * browsers report the device OS too. The `native` flag (set elsewhere from
 * isNativeApp()) is what distinguishes the native shell from mobile web:
 * native app = platform ios/android AND native=true; mobile web = same
 * platform but native=false.
 *
 * Inside the shell, OS detection is driven primarily by the native UA suffix
 * ('SparkPlugNativeApp/ios' | '/android'); older shell builds that append only
 * the bare 'SparkPlugNativeApp' fall back to base-UA sniffing.
 */
export const getPlatform = (): Platform => {
    const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';

    if (isNativeApp()) {
        // Primary: explicit OS suffix from the native shell's user-agent.
        if (ua.includes('SparkPlugNativeApp/ios')) return 'ios';
        if (ua.includes('SparkPlugNativeApp/android')) return 'android';

        // Older shell builds without the OS suffix: sniff the base UA, and
        // default to ios when undetermined (iOS WebViews historically omit a
        // clear marker more often than Android does).
        return sniffOsFromUa(ua) ?? 'ios';
    }

    if (isPWA()) return 'pwa';

    // Plain web browser: report the device OS so iPhone/Android web traffic is
    // attributed to ios/android (paired with native=false), falling back to
    // 'browser' for desktop (and iPadOS Safari, which sends a macOS UA).
    return sniffOsFromUa(ua) ?? 'browser';
};

export interface NativeShellInfo {
    native_app_version?: string;
    native_build_number?: string;
}

/**
 * When running inside the native shell, extracts the native app version and
 * build number that the shell encodes into its user-agent suffix
 * ('SparkPlugNativeApp/<os>/<version>/<build>'). Lets web analytics events be
 * segmented by the native binary they ran in.
 *
 * Returns an empty object when not in the shell, or for older native builds
 * whose UA suffix predates the version/build segments — so spreading it is
 * always safe.
 */
export const getNativeShellInfo = (): NativeShellInfo => {
    const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
    const match = ua.match(/SparkPlugNativeApp\/(?:ios|android)\/([^/\s]+)\/([^/\s]+)/i);
    if (!match) return {};

    const [, version, build] = match;
    const info: NativeShellInfo = {};
    if (version && version !== 'unknown') info.native_app_version = version;
    if (build && build !== 'unknown') info.native_build_number = build;
    return info;
};
